import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { downloadSheet } from '../lib/exportSheet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Trash2, Download, RefreshCw, Loader2, X, Upload, CheckCircle, Search, SlidersHorizontal, Plus, Pencil, Check } from 'lucide-react';

/**
 * First field of a CSV line, honouring quoting.
 *
 * Splitting on /[,;\t]/ and trimming was not enough: a quoted field keeps its quotes, so
 * a cell exported as "0071004A83" was imported — and stored — as that literal 12-character
 * string, which matches no device anywhere.
 */
export const firstCsvField = (line) => {
  const text = String(line ?? '').replace(/\r$/, '');
  if (text[0] !== '"') return text.split(/[,;\t]/)[0];

  let field = '';
  for (let i = 1; i < text.length; i += 1) {
    if (text[i] !== '"') {
      field += text[i];
    } else if (text[i + 1] === '"') {
      field += '"';   // "" is an escaped quote inside a quoted field
      i += 1;
    } else {
      break;          // closing quote ends the field
    }
  }
  return field;
};

/** Trim a serial and peel off any quote characters that survived a file round trip. */
export const normalizeSerial = (raw) => {
  let serial = String(raw ?? '').trim().replace(/^﻿/, '');
  while (serial.length >= 2 && serial[0] === serial[serial.length - 1] && (serial[0] === '"' || serial[0] === "'")) {
    serial = serial.slice(1, -1).trim();
  }
  return serial;
};

const DeviceGroups = ({ onNavigateToFleet }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editorDevices, setEditorDevices] = useState([]);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState(null);
  const [editorSuccess, setEditorSuccess] = useState(null);
  const [newDeviceSerial, setNewDeviceSerial] = useState('');
  const [importingDevices, setImportingDevices] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [editorName, setEditorName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [groupProgress, setGroupProgress] = useState({});
  const [progressLoading, setProgressLoading] = useState(false);
  const [renamingGroup, setRenamingGroup] = useState(null); // { name: string, draft: string, saving: bool, error: string|null }
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingGroup, setDeletingGroup] = useState(false);
  const fileInputRef = useRef(null);

  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groups;
    const term = searchTerm.toLowerCase();
    return groups.filter(group =>
      group.name.toLowerCase().includes(term) ||
      (group.devices || []).some(device => device.toLowerCase().includes(term))
    );
  }, [groups, searchTerm]);

  const fleetSummary = useMemo(() => {
    const activeFleets = groups.length;
    const totalAssets = groups.reduce((sum, group) => sum + (group.device_count || (group.devices?.length || 0)), 0);
    const progressValues = [];
    let offlineDevices = 0;

    groups.forEach(group => {
      const summary = groupProgress[group.name];
      if (summary && summary.total) {
        progressValues.push(summary.completed / summary.total);
        offlineDevices += summary.offline || 0;
      }
    });

    const avgMigration = progressValues.length === 0
      ? 0
      : Math.round((progressValues.reduce((sum, value) => sum + value, 0) / progressValues.length) * 1000) / 10;

    return { activeFleets, totalAssets, avgMigration, offlineDevices };
  }, [groups, groupProgress]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/device-groups');
      setGroups(response.data.groups || []);
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const parseDeviceFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let devices = [];
          const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

          if (extension === '.csv') {
            const text = new TextDecoder().decode(e.target.result);
            const lines = text.split('\n').filter(line => line.trim());
            const startIndex = lines[0]?.toLowerCase().includes('serial') ? 1 : 0;
            devices = lines.slice(startIndex).map(line => ({
              serial: normalizeSerial(firstCsvField(line)),
            })).filter(d => d.serial);
          } else {
            const workbook = XLSX.read(e.target.result, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            // raw:false + a text default keeps a numeric-looking serial as the string the
            // sheet displays, instead of handing back a JS number with its leading zeros
            // already gone.
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
            const startIndex = data[0] && String(data[0][0]).toLowerCase().includes('serial') ? 1 : 0;
            devices = data.slice(startIndex).map(row => ({
              serial: normalizeSerial(row[0]),
            })).filter(d => d.serial);
          }

          resolve(devices);
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const openEditor = async (groupName) => {
    setEditorLoading(true);
    setEditorError(null);
    setEditorSuccess(null);
    setImportMessage(null);
    setEditingGroup({ name: groupName, isNew: false });
    setEditorName(groupName);

    try {
      const response = await axios.get(`/api/device-groups/${encodeURIComponent(groupName)}`);
      const devices = (response.data.devices || []).map(d =>
        typeof d === 'string' ? { serial: d.split(':')[0] } : { serial: d.serial || d }
      );
      setEditorDevices(devices);
    } catch (err) {
      setEditorError(err.response?.data?.error || 'Failed to load group');
    } finally {
      setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    setEditingGroup(null);
    setEditorDevices([]);
    setEditorError(null);
    setEditorSuccess(null);
    setImportMessage(null);
    setNewDeviceSerial('');
    setEditorName('');
    setDeleteConfirmOpen(false);
    setDeleteConfirmText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreateFleet = () => {
    setEditingGroup({ name: '', isNew: true });
    setEditorDevices([]);
    setEditorName('');
    setEditorError(null);
    setEditorSuccess(null);
    setImportMessage(null);
  };

  const handleAddDevice = () => {
    const serial = normalizeSerial(newDeviceSerial);
    if (!serial) {
      setEditorError('Enter a valid serial number');
      return;
    }

    const exists = editorDevices.some(d => d.serial === serial);
    if (exists) {
      setEditorError('Device already in group');
      return;
    }

    setEditorDevices(prev => [...prev, { serial }]);
    setNewDeviceSerial('');
    setEditorError(null);
  };

  const handleRemoveDevice = (serial) => {
    setEditorDevices(prev => prev.filter(d => d.serial !== serial));
  };

  const handleImportDevices = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(extension)) {
      setImportMessage('Unsupported file type. Use CSV or Excel.');
      return;
    }

    setImportingDevices(true);
    setImportMessage(null);
    setEditorError(null);

    try {
      const imported = await parseDeviceFile(file);

      setEditorDevices(prev => {
        const existing = new Set(prev.map(d => d.serial));
        const merged = [...prev];
        imported.forEach(device => {
          if (!existing.has(device.serial)) {
            existing.add(device.serial);
            merged.push(device);
          }
        });
        return merged;
      });

      setImportMessage(`Imported ${imported.length} device${imported.length === 1 ? '' : 's'}. Duplicates were ignored.`);
      fileInputRef.current.value = '';
    } catch (err) {
      setImportMessage(err.message || 'Failed to import devices');
    } finally {
      setImportingDevices(false);
    }
  };

  const handleSaveGroup = async () => {
    if (!editingGroup) return;
    const targetName = (editingGroup.isNew ? editorName : editingGroup.name || editorName).trim();
    if (!targetName) {
      setEditorError('Fleet name is required.');
      return;
    }
    if (editorDevices.length === 0) {
      setEditorError('Add at least one device to the group.');
      return;
    }

    setEditorSaving(true);
    setEditorError(null);
    setEditorSuccess(null);

    try {
      const payload = editorDevices.map(d => ({ serial: d.serial }));

      if (editingGroup.isNew) {
        await axios.post('/api/device-groups', {
          name: targetName,
          devices: payload
        });
        setGroups(prev => [...prev, {
          name: targetName,
          device_count: editorDevices.length,
          devices: payload.map(d => d.serial)
        }]);
        setEditingGroup({ name: targetName, isNew: false });
      } else {
        const newName = editorName.trim();
        const nameChanged = newName !== editingGroup.name;
        await axios.put(`/api/device-groups/${encodeURIComponent(editingGroup.name)}`, {
          ...(nameChanged && { name: newName }),
          devices: payload,
        });
        setGroups(prev => prev.map(group =>
          group.name === editingGroup.name
            ? { ...group, name: newName, device_count: editorDevices.length, devices: payload.map(d => d.serial) }
            : group
        ));
        if (nameChanged) {
          setGroupProgress(prev => {
            const next = { ...prev };
            next[newName] = next[editingGroup.name];
            delete next[editingGroup.name];
            return next;
          });
        }
      }

      setEditorName(targetName);
      setEditorSuccess('Group saved successfully');
      closeEditor();
    } catch (err) {
      setEditorError(err.response?.data?.error || 'Failed to save group');
    } finally {
      setEditorSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!editingGroup || editingGroup.isNew) return;
    if (deleteConfirmText.trim().toLowerCase() !== 'delete') return;

    setDeletingGroup(true);
    setEditorError(null);

    try {
      await axios.delete(`/api/device-groups/${encodeURIComponent(editingGroup.name)}`);
      setGroups(prev => prev.filter(group => group.name !== editingGroup.name));
      closeEditor();
    } catch (err) {
      setEditorError(err.response?.data?.error || 'Failed to delete group');
    } finally {
      setDeletingGroup(false);
    }
  };

  const getProgressForGroup = (groupName, totalDevices) => {
    const summary = groupProgress[groupName];
    if (!summary) {
      if (!totalDevices) return 0;
      return 0;
    }
    if (!summary.total) return 0;
    return Math.round((summary.completed / summary.total) * 100);
  };

  // The fleet list endpoint returns counts only — it never carried a `devices` key — so
  // building the export from `groups` produced a header row and nothing else. The pairs
  // come from the backend in one query instead.
  const handleExportAll = async () => {
    if (groups.length === 0) return;
    setExporting(true);
    setExportError(null);
    try {
      const response = await axios.get('/api/device-groups/export');
      const rows = response.data.rows || [];
      if (rows.length === 0) {
        setExportError('No devices to export — every fleet is empty.');
        return;
      }
      downloadSheet({
        filename: 'fleet-groups.xlsx',
        sheetName: 'Fleets',
        headers: ['fleet_name', 'device_serial'],
        rows: rows.map(row => [row.fleet_name, row.device_serial]),
        textColumns: ['device_serial'],
      });
    } catch (err) {
      console.error('Failed to export fleets:', err);
      setExportError(err.response?.data?.error || 'Failed to export fleets');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      if (!groups || groups.length === 0) {
        setGroupProgress({});
        return;
      }
      setProgressLoading(true);
      try {
        const entries = await Promise.all(groups.map(async (group) => {
          try {
            const response = await axios.get(`/api/onboarding/results/${encodeURIComponent(group.name)}`);
            const completion = calculateCompletionSummary(group, response.data);
            return [group.name, completion];
          } catch (err) {
            console.error('Failed to load onboarding results for group', group.name, err);
            return [group.name, {
              completed: 0,
              total: group.devices?.length || group.device_count || 0,
              offline: 0
            }];
          }
        }));
        if (!cancelled) {
          const progressMap = {};
          entries.forEach(([name, data]) => {
            progressMap[name] = data;
          });
          setGroupProgress(progressMap);
        }
      } finally {
        if (!cancelled) {
          setProgressLoading(false);
        }
      }
    };

    fetchProgress();

    return () => {
      cancelled = true;
    };
  }, [groups]);

  const handleStartRename = (groupName) => {
    setRenamingGroup({ name: groupName, draft: groupName, saving: false, error: null });
  };

  const handleCancelRename = () => {
    setRenamingGroup(null);
  };

  const handleConfirmRename = async () => {
    const newName = renamingGroup.draft.trim();
    if (!newName) {
      setRenamingGroup(prev => ({ ...prev, error: 'Fleet name cannot be empty.' }));
      return;
    }
    if (newName === renamingGroup.name) {
      setRenamingGroup(null);
      return;
    }
    setRenamingGroup(prev => ({ ...prev, saving: true, error: null }));
    try {
      await axios.put(`/api/device-groups/${encodeURIComponent(renamingGroup.name)}`, { name: newName });
      setGroups(prev => prev.map(g => g.name === renamingGroup.name ? { ...g, name: newName } : g));
      setGroupProgress(prev => {
        const next = { ...prev };
        next[newName] = next[renamingGroup.name];
        delete next[renamingGroup.name];
        return next;
      });
      setRenamingGroup(null);
    } catch (err) {
      setRenamingGroup(prev => ({
        ...prev,
        saving: false,
        error: err.response?.data?.error || 'Failed to rename fleet.',
      }));
    }
  };

  return (
    <>
    <section className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Manage Fleet Groups</p>
          <h2 className="text-2xl font-semibold text-slate-900 mt-1">Monitor rollout progress across fleets</h2>
          <p className="text-sm text-slate-500 mt-1">Track assets, migration completion and quickly configure each fleet.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleExportAll}
            disabled={groups.length === 0 || exporting}
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export All'}
          </Button>
          {exportError && (
            <p role="alert" className="self-center text-sm text-red-600">{exportError}</p>
          )}
          <Button className="flex items-center gap-2" onClick={handleCreateFleet}>
            <Plus className="w-4 h-4" />
            Add New Fleet
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={fetchGroups}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Active Fleets" value={fleetSummary.activeFleets} />
        <SummaryCard label="Total Assets" value={fleetSummary.totalAssets.toLocaleString()} />
        <SummaryCard label="Avg Migration %" value={`${fleetSummary.avgMigration.toFixed(1)}%`} accent />
        <SummaryCard label="Offline Devices" value={fleetSummary.offlineDevices} warning />
      </div>

      <Card className="border-slate-200">
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search by fleet name or device ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <Button variant="outline" className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Visibility
            </Button>
          </div>

          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-12 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-widest">
              <div className="col-span-4 px-6 py-3">Fleet Name</div>
              <div className="col-span-2 px-4 py-3">Device Count</div>
              <div className="col-span-4 px-4 py-3">Migration Progress</div>
              <div className="col-span-2 px-4 py-3 text-right">Actions</div>
            </div>
            {(loading || progressLoading) ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> {loading ? 'Loading fleets...' : 'Syncing migration progress...'}
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">No fleets match your search.</div>
            ) : (
              filteredGroups.map(group => {
                const deviceCount = group.device_count ?? (group.devices?.length || 0);
                const progress = getProgressForGroup(group.name, deviceCount);
                const summary = groupProgress[group.name];
                return (
                  <div
                    key={group.name}
                    className="grid grid-cols-12 border-t border-slate-100 bg-white hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => onNavigateToFleet && onNavigateToFleet(group.name)}
                  >
                    <div className="col-span-4 px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      {renamingGroup?.name === group.name ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <input
                              autoFocus
                              className="text-sm font-semibold border border-slate-300 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={renamingGroup.draft}
                              onChange={(e) => setRenamingGroup(prev => ({ ...prev, draft: e.target.value, error: null }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleConfirmRename();
                                if (e.key === 'Escape') handleCancelRename();
                              }}
                              disabled={renamingGroup.saving}
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-emerald-600 hover:text-emerald-700 shrink-0"
                              onClick={handleConfirmRename}
                              disabled={renamingGroup.saving}
                            >
                              {renamingGroup.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-slate-500 hover:text-slate-700 shrink-0"
                              onClick={handleCancelRename}
                              disabled={renamingGroup.saving}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          {renamingGroup.error && (
                            <p className="text-xs text-red-600">{renamingGroup.error}</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group/name">
                          <div>
                            <p className="font-semibold text-slate-900">{group.name}</p>
                            <p className="text-xs text-slate-500">ID: {group.name.replace(/\s+/g, '_').toUpperCase()}</p>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="opacity-0 group-hover/name:opacity-100 text-slate-400 hover:text-slate-700 transition-opacity"
                            onClick={() => handleStartRename(group.name)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 px-4 py-4">
                      <p className="font-medium">{deviceCount.toLocaleString()}</p>
                    </div>
                    <div className="col-span-4 px-4 py-4">
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{progress}% Complete</span>
                        {summary && summary.total > 0 && (
                          <span className="text-slate-400">{summary.completed}/{summary.total}</span>
                        )}
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-blue-500" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                    <div className="col-span-2 px-4 py-4 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        onClick={() => openEditor(group.name)}
                        className="bg-slate-900 hover:bg-slate-800"
                      >
                        Configure
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </section>
    <DeviceGroupEditor
      editingGroup={editingGroup}
      editorDevices={editorDevices}
      editorLoading={editorLoading}
      editorSaving={editorSaving}
      editorError={editorError}
      editorSuccess={editorSuccess}
      newDeviceSerial={newDeviceSerial}
      importingDevices={importingDevices}
      importMessage={importMessage}
      fileInputRef={fileInputRef}
      editorName={editorName}
      setEditorName={setEditorName}
      setNewDeviceSerial={setNewDeviceSerial}
      handleAddDevice={handleAddDevice}
      handleRemoveDevice={handleRemoveDevice}
      handleImportDevices={handleImportDevices}
      handleSaveGroup={handleSaveGroup}
      closeEditor={closeEditor}
      deleteConfirmOpen={deleteConfirmOpen}
      deleteConfirmText={deleteConfirmText}
      deletingGroup={deletingGroup}
      setDeleteConfirmOpen={setDeleteConfirmOpen}
      setDeleteConfirmText={setDeleteConfirmText}
      handleDeleteGroup={handleDeleteGroup}
    />
    </>
  );
};

const SummaryCard = ({ label, value, accent, warning }) => (
  <div className={`rounded-2xl border ${warning ? 'border-red-100 bg-red-50' : accent ? 'border-blue-100 bg-blue-50' : 'border-slate-200 bg-white'} p-4 shadow-sm`}>
    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`text-3xl font-bold mt-2 ${warning ? 'text-red-600' : accent ? 'text-blue-600' : 'text-slate-900'}`}>{value}</p>
  </div>
);

const calculateCompletionSummary = (group, onboardingData) => {
  // /api/device-groups returns device_count but not the devices array, so use device_count for total
  const total = group.device_count ?? (group.devices?.length || 0);
  if (!total) {
    return { completed: 0, total: 0, offline: 0 };
  }

  const results = onboardingData?.results || [];

  let completed = 0;
  let offline = 0;
  results.forEach(result => {
    if (result.status === 'complete' || result.is_complete) {
      completed += 1;
    }
    if (result.status === 'deactivated') {
      offline += 1;
    }
  });

  return { completed, total, offline };
};

const DeviceListItem = ({ device, onRemove }) => {
  return (
    <div className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm">
      <p className="font-mono text-xs sm:text-sm">{device.serial}</p>
      <Button
        variant="ghost"
        size="icon"
        className="text-red-500 hover:text-red-600"
        onClick={() => onRemove(device.serial)}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

const DeviceGroupEditor = ({
  editingGroup,
  editorDevices,
  editorLoading,
  editorSaving,
  editorError,
  editorSuccess,
  editorName,
  newDeviceSerial,
  importingDevices,
  importMessage,
  fileInputRef,
  setEditorName,
  setNewDeviceSerial,
  handleAddDevice,
  handleRemoveDevice,
  handleImportDevices,
  handleSaveGroup,
  closeEditor,
  deleteConfirmOpen,
  deleteConfirmText,
  deletingGroup,
  setDeleteConfirmOpen,
  setDeleteConfirmText,
  handleDeleteGroup
}) => {
  if (!editingGroup) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{editingGroup.isNew ? 'Create fleet group' : `Edit group: ${editingGroup.name}`}</h2>
            <p className="text-sm text-muted-foreground">Add or remove devices from this fleet.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={closeEditor}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {editorLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading devices...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fleetName">Fleet name</Label>
                  <Input
                    id="fleetName"
                    placeholder="e.g., Western Logistics"
                    value={editorName}
                    onChange={(e) => setEditorName(e.target.value)}
                  />
                </div>
              </div>

              {editorError && (
                <div className="p-3 border border-red-200 bg-red-50 text-sm text-red-700 rounded-lg">
                  {editorError}
                </div>
              )}
              {editorSuccess && (
                <div className="p-3 border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {editorSuccess}
                </div>
              )}
              <div>
                <h3 className="font-medium mb-2">Devices ({editorDevices.length})</h3>
                {editorDevices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No devices in this group yet.</p>
                ) : (
                  <div className="grid gap-2 max-h-48 overflow-y-auto pr-1">
                    {editorDevices.map(device => (
                      <DeviceListItem key={device.serial} device={device} onRemove={handleRemoveDevice} />
                    ))}
                  </div>
                )}
              </div>

              <div className="border rounded-xl p-4 space-y-4">
                <h4 className="font-medium">Add device manually</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="newSerial">Serial number</Label>
                    <Input
                      id="newSerial"
                      placeholder="SN-STREAM-001"
                      value={newDeviceSerial}
                      onChange={(e) => setNewDeviceSerial(e.target.value)}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button className="w-full" onClick={handleAddDevice}>
                      Add device
                    </Button>
                  </div>
                </div>
              </div>

              <div className="border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h4 className="font-medium">Import from file</h4>
                    <p className="text-sm text-muted-foreground">Upload CSV or Excel with serial numbers.</p>
                  </div>
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={handleImportDevices}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={importingDevices}
                    >
                      <Upload className="w-4 h-4" />
                      {importingDevices ? 'Importing...' : 'Select file'}
                    </Button>
                  </div>
                </div>
                {importMessage && (
                  <p className="text-sm text-muted-foreground">{importMessage}</p>
                )}
              </div>

              {!editingGroup.isNew && deleteConfirmOpen && (
                <div className="border border-red-200 bg-red-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-red-700">
                    This will permanently delete <strong>"{editingGroup.name}"</strong> and all{' '}
                    {editorDevices.length} device{editorDevices.length === 1 ? '' : 's'} in it. This cannot be undone.
                  </p>
                  <Input
                    placeholder='Type "delete" to confirm'
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                  />
                  <div className="flex items-center justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmText(''); }}
                      disabled={deletingGroup}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteGroup}
                      disabled={deletingGroup || deleteConfirmText.trim().toLowerCase() !== 'delete'}
                      className="flex items-center gap-2"
                    >
                      {deletingGroup && <Loader2 className="w-4 h-4 animate-spin" />}
                      Delete fleet
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <div>
                  {!editingGroup.isNew && !deleteConfirmOpen && (
                    <Button variant="destructive" onClick={() => setDeleteConfirmOpen(true)} disabled={editorSaving}>
                      Delete fleet
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={closeEditor} disabled={editorSaving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveGroup} disabled={editorSaving} className="flex items-center gap-2">
                    {editorSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                    Save group
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceGroups;
